import { useEffect, useState } from 'react';
import api from '../../../lib/api';
import { Badge } from '../../../components/ui/badge';
import { fmtDate } from './periodUtils';
import { ClipboardList, Loader2, UserCheck, UserX } from 'lucide-react';

// Shows the trainer's DPR (daily attendance) for one batch on one day.
export default function DprDayDetail({ batchId, date, defaultOpen = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!batchId || !date || !defaultOpen) return;
    setLoading(true);
    api.get('/training/audit/dpr-day', { params: { batch_id: batchId, date } })
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [batchId, date, defaultOpen]);

  if (!defaultOpen) return null;

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="dpr-day-detail">
      <div className="flex items-center justify-between px-3.5 py-2.5 bg-slate-50 border-b border-slate-100">
        <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-600">
          <ClipboardList className="w-3.5 h-3.5 text-violet-600" />DPR — {fmtDate(date)}
        </span>
        {data?.found && (
          <span className="flex items-center gap-2 text-[11px]">
            <Badge className={`border-0 ${data.class_taken ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{data.class_taken ? 'Class Held' : 'Not Conducted'}</Badge>
            {data.mode && <span className="text-slate-400">{data.mode}</span>}
          </span>
        )}
      </div>
      <div className="p-3.5">
        {loading ? (
          <div className="py-4 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
        ) : !data?.found ? (
          <p className="text-[12px] text-rose-600 flex items-center gap-1.5"><UserX className="w-3.5 h-3.5" />No DPR was submitted for this day.</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-3">
              <span className="inline-flex items-center gap-1 text-[11px] bg-emerald-50 text-emerald-700 rounded-md px-2 py-1"><UserCheck className="w-3 h-3" />{data.present_count} present</span>
              <span className="inline-flex items-center gap-1 text-[11px] bg-rose-50 text-rose-700 rounded-md px-2 py-1"><UserX className="w-3 h-3" />{data.absent_count} absent</span>
              <span className="inline-flex items-center gap-1 text-[11px] bg-slate-100 text-slate-600 rounded-md px-2 py-1">{data.strength} on roll</span>
              {data.timing && <span className="inline-flex items-center text-[11px] bg-slate-100 text-slate-600 rounded-md px-2 py-1">{data.timing}</span>}
            </div>
            {data.not_taken_reason && <p className="text-[12px] text-amber-700 mb-2">Reason not conducted: <span className="font-medium">{data.not_taken_reason}</span></p>}
            {data.comments && <p className="text-[12px] text-slate-600 mb-2">Trainer notes: {data.comments}</p>}
            {data.students.length > 0 ? (
              <div className="border border-slate-100 rounded-lg overflow-auto max-h-52">
                <table className="w-full text-[12px]">
                  <thead className="sticky top-0 bg-slate-50"><tr className="text-left text-slate-500"><th className="py-1.5 px-2">#</th><th className="py-1.5 px-2">Student</th><th className="py-1.5 px-2">Enrolment</th><th className="py-1.5 px-2">Status</th></tr></thead>
                  <tbody>
                    {data.students.map((s, i) => {
                      const present = (s.status || '').toLowerCase() === 'present';
                      return (
                        <tr key={s.student_id || i} className="border-b border-slate-50 last:border-0">
                          <td className="py-1.5 px-2 text-slate-400">{i + 1}</td>
                          <td className="py-1.5 px-2 font-medium text-slate-700">{s.name || '—'}</td>
                          <td className="py-1.5 px-2 font-mono text-slate-500">{s.enrollment_no || '—'}</td>
                          <td className="py-1.5 px-2"><Badge className={`border-0 text-[10px] ${present ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>{present ? 'Present' : 'Absent'}</Badge></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-[12px] text-slate-400">No student attendance recorded.</p>}
          </>
        )}
      </div>
    </div>
  );
}
